import { useCallback, useMemo, useState } from 'react';
import SummaryDashboard from '../components/snoop/SummaryDashboard';
import MappingAddModal from '../components/snoop/MappingAddModal';
import {
  createSnoopMapping,
  deleteSnoopMapping,
  fetchSnoopMapping,
  updateSnoopMappingStatus,
} from '../api/snoopClient';
import { SNOOP_STATUS_OPTIONS } from '../utils/snoop/statusOptions';

const addBtnClass =
  'inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50';

export default function SnoopMappingPage() {
  const [reloadNonce, setReloadNonce] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const bump = useCallback(() => setReloadNonce((n) => n + 1), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(() => fetchSnoopMapping(), [reloadNonce]);

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'snoop_mapping',
        uidColumn: 'IG User ID',
        options: SNOOP_STATUS_OPTIONS,
        timestampColumn: 'Updated At',
        allowBlank: false,
        onSave: async ({ status, uid, row }) => {
          await updateSnoopMappingStatus({
            igUserId: uid,
            status,
            igUsername: row?.['IG Username'],
            igFullName: row?.['IG Full Name'],
          });
        },
      },
    }),
    []
  );

  const onDeleteRow = useCallback(
    async (row) => {
      const igUserId = String(row?.['IG User ID'] ?? '')
        .trim()
        .replace(/\.0$/, '');
      const igUsername = String(row?.['IG Username'] ?? '')
        .trim()
        .replace(/^@/, '');
      await deleteSnoopMapping({
        igUserId: igUserId || undefined,
        igUsername: igUsername || undefined,
      });
      bump();
    },
    [bump]
  );

  const searchActions = (
    <button type="button" className={addBtnClass} onClick={() => setAddOpen(true)}>
      Add Mapping
    </button>
  );

  return (
    <>
      <SummaryDashboard
        title="Snoop Mapping"
        subtitle="Snoop account status"
        fetchData={fetchData}
        defaultSort={[
          { column: 'Updated At', direction: 'desc' },
          { column: 'IG Username', direction: 'asc' },
        ]}
        searchPlaceholder="Search username, status, UID…"
        searchActions={searchActions}
        editableStatus={editableStatus}
        onDeleteRow={onDeleteRow}
        layout="mapping"
        linkLabel="IG"
        tableKey={`snoop-${reloadNonce}`}
      />
      <MappingAddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add snoop mapping"
        submitLabel="Save mapping"
        fields={[
          {
            name: 'igUsername',
            label: 'IG Username',
            required: true,
            placeholder: 'username',
          },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            required: true,
            defaultValue: 'CORE',
            options: SNOOP_STATUS_OPTIONS,
          },
          { name: 'igUserId', label: 'IG User ID', placeholder: 'Numeric UID…' },
          { name: 'igFullName', label: 'IG Full Name', placeholder: 'Display name…' },
        ]}
        onSubmit={async (values) => {
          await createSnoopMapping({
            igUsername: values.igUsername,
            status: values.status,
            igUserId: values.igUserId,
            igFullName: values.igFullName,
          });
          bump();
        }}
      />
    </>
  );
}
